import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { getConversations, ConversationDto } from '@/store/slices/chatSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';
import LoadingScreen from '@/components/common/LoadingScreen';
import twilioChat from '@/services/twilioChat';

interface RowProps {
  conversation: ConversationDto;
  meEmployeeId?: number;
  onPress: () => void;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const ConversationRow: React.FC<RowProps> = ({ conversation, meEmployeeId, onPress }) => {
  // DIRECT conversations: title = the other party's name.
  // GROUP / BROADCAST: title = conversation.name.
  const otherMember =
    conversation.type === 'DIRECT'
      ? conversation.members.find((m) => m.employeeId !== meEmployeeId)
      : null;
  const title = otherMember?.fullName || conversation.name || 'Conversation';
  const subtitle =
    conversation.type === 'DIRECT'
      ? 'Direct message'
      : `${conversation.members.length} ${
          conversation.members.length === 1 ? 'member' : 'members'
        }`;
  const modified = conversation.modifiedOn ? parseISO(conversation.modifiedOn) : null;
  const stamp = modified ? format(modified, 'MMM d') : '';

  return (
    <TouchableOpacity style={styles.row} activeOpacity={0.7} onPress={onPress}>
      <View style={styles.avatar}>
        <Text style={styles.avatarText}>{initials(title)}</Text>
      </View>
      <View style={styles.rowBody}>
        <View style={styles.rowHead}>
          <Text style={styles.rowTitle} numberOfLines={1}>
            {title}
          </Text>
          {!!stamp && <Text style={styles.rowStamp}>{stamp}</Text>}
        </View>
        <Text style={styles.rowSubtitle} numberOfLines={1}>
          {subtitle}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const MessagesScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { conversations, isLoading } = useSelector((state: RootState) => state.chat);

  const meEmployeeId = user?.employee?.id;

  const [sessionError, setSessionError] = useState<string | null>(null);

  const load = useCallback(() => {
    setSessionError(null);
    dispatch(getConversations());
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh the conversation list when Twilio tells us a conversation
  // changed — covers rename, archive, new participant, last-message bump,
  // etc. without forcing the user to pull-to-refresh.
  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const onUpdated = () => {
      if (!cancelled) {
        dispatch(getConversations());
      }
    };
    twilioChat
      .getClient()
      .then((c) => {
        if (cancelled) {
          return;
        }
        c.on('conversationUpdated', onUpdated);
        c.on('conversationAdded', onUpdated);
        c.on('conversationRemoved', onUpdated);
        unsub = () => {
          c.off('conversationUpdated', onUpdated);
          c.off('conversationAdded', onUpdated);
          c.off('conversationRemoved', onUpdated);
        };
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [dispatch]);

  useEffect(() => {
    const unsub = twilioChat.onError((err) => setSessionError(err.message));
    return unsub;
  }, []);

  const sorted = useMemo(
    () =>
      [...conversations].sort((a, b) => {
        const at = a.modifiedOn ? Date.parse(a.modifiedOn) : 0;
        const bt = b.modifiedOn ? Date.parse(b.modifiedOn) : 0;
        return bt - at;
      }),
    [conversations]
  );

  if (isLoading && conversations.length === 0) {
    return <LoadingScreen message="Loading messages…" />;
  }

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={load}
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      <View style={styles.header}>
        <Text style={styles.kicker}>STAFF</Text>
        <Text style={styles.headline}>MESSAGES</Text>
        <View style={styles.rule} />
      </View>

      {sessionError && (
        <View style={styles.sessionBanner}>
          <Icon name="alert-circle" size={18} color={colors.warn} />
          <Text style={styles.sessionBannerText}>{sessionError}</Text>
          <TouchableOpacity onPress={load} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={styles.sessionBannerAction}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      <TouchableOpacity
        style={styles.newBtn}
        activeOpacity={0.85}
        onPress={() => navigation.navigate('NewConversation')}>
        <Icon name="add-circle" size={22} color={colors.accent} />
        <Text style={styles.newBtnText}>NEW CONVERSATION</Text>
      </TouchableOpacity>

      {sorted.length === 0 ? (
        <View style={styles.empty}>
          <View style={styles.emptyMark}>
            <Icon name="chatbubbles-outline" size={32} color={colors.inkFaint} />
          </View>
          <Text style={styles.emptyTitle}>No conversations yet</Text>
          <Text style={styles.emptyBody}>
            Tap "New conversation" to start chatting with another staff member.
          </Text>
        </View>
      ) : (
        sorted.map((c) => (
          <ConversationRow
            key={c.id}
            conversation={c}
            meEmployeeId={meEmployeeId}
            onPress={() =>
              navigation.navigate('Conversation', {
                twilioSid: c.twilioConversationSid,
                title:
                  c.type === 'DIRECT'
                    ? c.members.find((m) => m.employeeId !== meEmployeeId)?.fullName
                    : c.name,
              })
            }
          />
        ))
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },

  header: { marginBottom: space.lg },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 56,
    lineHeight: 58,
    color: colors.ink,
    letterSpacing: -1,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
  },

  sessionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.sm,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    marginBottom: space.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.warn,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderTopColor: colors.line,
    borderRightColor: colors.line,
    borderBottomColor: colors.line,
  },
  sessionBannerText: {
    flex: 1,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.ink,
    marginLeft: space.sm,
  },
  sessionBannerAction: {
    fontFamily: font.bodyBold,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: colors.accent,
    marginLeft: space.sm,
  },

  newBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 54,
    borderRadius: radius.md,
    backgroundColor: colors.surface,
    borderWidth: 2,
    borderColor: colors.accent,
    marginBottom: space.lg,
  },
  newBtnText: {
    fontFamily: font.display,
    fontSize: 21,
    letterSpacing: 1,
    color: colors.accent,
    marginLeft: space.sm,
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  avatarText: {
    fontFamily: font.bodyBold,
    fontSize: 16,
    letterSpacing: 0.5,
    color: colors.accentDark,
  },
  rowBody: { flex: 1 },
  rowHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  rowTitle: {
    flex: 1,
    fontFamily: font.bodyBold,
    fontSize: 16,
    color: colors.ink,
  },
  rowStamp: {
    fontFamily: font.bodyMed,
    fontSize: 12,
    color: colors.inkFaint,
    marginLeft: space.sm,
  },
  rowSubtitle: {
    fontFamily: font.bodyMed,
    fontSize: 13.5,
    color: colors.inkSoft,
    marginTop: 2,
  },

  empty: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
  },
  emptyMark: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.bg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.sm,
  },
  emptyTitle: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
  },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    textAlign: 'center',
    marginTop: 2,
  },
});

export default MessagesScreen;
